import { type Detection, languages } from "@codeshare/languages";
import { BUILD_INFO } from "../build-info";
import type { EditorStatus } from "../hooks/use-editor-status";
import type { RoomState } from "../session/room-controller";
import { Icon } from "./Icon";

const SOURCE_LABEL: Record<Detection["source"], string> = {
  user: "set manually",
  filename: "from file name",
  extension: "from extension",
  shebang: "from shebang",
  modeline: "from modeline",
  content: "detected from content",
  default: "not detected",
};

interface StatusBarProps {
  language: Detection;
  editor: EditorStatus;
  /** Go to line (clicking the cursor position). */
  onGoToLine: () => void;
  room: RoomState;
  /** Opens people & chat (clicking the room indicator). */
  onShowRoom: () => void;
  /** Opens the language picker (clicking the language). */
  onChangeLanguage: () => void;
  /** The room's compiler for this language, when there's a choice (OCaml or OxCaml). */
  toolchain?: { label: string; onChange: () => void };
}

function roomLabel(room: RoomState): string | undefined {
  if (room.status === "solo") return undefined;
  if (room.status === "joining") return "Joining room…";
  if (room.status === "error") return "Room error";
  const ready = room.peers.filter((p) => p.state === "ready").length;
  return ready === 0 ? "In room · alone" : `In room · ${ready} ${ready === 1 ? "peer" : "peers"}`;
}

/**
 * VS Code's status bar: the room on the left (like its remote indicator, colored while you're
 * in one), the file's language and the build on the right.
 */
export function StatusBar(props: StatusBarProps) {
  const { language, editor, room, onShowRoom, onChangeLanguage, onGoToLine, toolchain } = props;
  const roomText = roomLabel(room);
  const name = languages.get(language.id)?.name ?? "Plain Text";
  return (
    <footer className="status-bar" data-room={room.status}>
      {roomText ? (
        <button
          type="button"
          className="room-indicator"
          data-testid="room-indicator"
          data-status={room.status}
          title="Peer to peer over WebRTC, found through Nostr relays. Click for people & chat."
          onClick={onShowRoom}
        >
          <Icon name="remote" size={14} />
          {roomText}
        </button>
      ) : (
        <span className="room-indicator" data-status="solo" title="Not in a room">
          <Icon name="remote" size={14} />
          Local
        </span>
      )}
      <span className="status-bar-spacer" />
      <button
        type="button"
        className="status-item"
        data-testid="cursor-status"
        title="Go to line/column"
        onClick={onGoToLine}
      >
        Ln {editor.line}, Col {editor.column}
        {editor.selected > 0 && ` (${editor.selected} selected)`}
      </button>
      <span className="status-item status-detail">
        {editor.insertSpaces ? "Spaces" : "Tab Size"}: {editor.tabSize}
      </span>
      <span className="status-item status-detail">UTF-8</span>
      <span className="status-item status-detail">{editor.eol}</span>
      <button
        type="button"
        className="status-item"
        data-testid="language-status"
        title={`Language ${SOURCE_LABEL[language.source]} (click to change)`}
        onClick={onChangeLanguage}
      >
        {name}
        <span className="language-source"> · {SOURCE_LABEL[language.source]}</span>
      </button>
      {toolchain && (
        <button
          type="button"
          className="status-item"
          data-testid="toolchain-status"
          title="Compiler for everyone in the room (click to change)"
          onClick={toolchain.onChange}
        >
          {toolchain.label}
        </button>
      )}
      <small
        className="status-item"
        data-testid="build-version"
        title={`Built ${new Date(BUILD_INFO.builtAt * 1000).toISOString()}`}
      >
        v{BUILD_INFO.version} · {BUILD_INFO.sha}
      </small>
    </footer>
  );
}
